// src/InstallationActionsModal.js
import React, { useState, useEffect, useRef } from "react";
import "./InstallationActionsModal.css";
import logger from "./utils/logger";
import { supportsLockdown } from "./utils/lockdownCompatibility";

const InstallationActionsModal = ({
  isOpen,
  onClose,
  onExecuteAction,
  deviceCount,
  eligibleForLockdown,
  eligibleForRelease,
  eligibleDevicesForLockdown = [],
  eligibleDevicesForRelease = [],
  installationName,
  allDeviceStatesLoaded = true, // OPTION 2: Add loading state prop
  onRetrySync, // FIX #2: Callback to retry sync
}) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [syncTimeout, setSyncTimeout] = useState(false); // FIX #2: Track if sync timed out
  const [loadingStartTime, setLoadingStartTime] = useState(null);
  const [selectedDeviceIds, setSelectedDeviceIds] = useState(new Set());
  const timeoutRef = useRef(null);
  const isInitializedRef = useRef(false);

  // FIX #2: Track loading state and set timeout
  useEffect(() => {
    if (isOpen && !allDeviceStatesLoaded && !syncTimeout) {
      // Start timeout when modal opens and states aren't loaded
      if (!loadingStartTime) {
        setLoadingStartTime(Date.now());
      }

      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set 20-second timeout
      timeoutRef.current = setTimeout(() => {
        setSyncTimeout(true);
        logger.warn(
          "[InstallationActionsModal] Sync timeout after 20 seconds"
        );
      }, 20000);
    } else if (allDeviceStatesLoaded) {
      // Clear timeout if loading completes
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setSyncTimeout(false);
      setLoadingStartTime(null);
    }

    // Cleanup on unmount or modal close
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isOpen, allDeviceStatesLoaded, syncTimeout, loadingStartTime]);

  // Reset state when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setShowComingSoon(false);
      setShowConfirmation(false);
      setSelectedAction(null);
      setSyncTimeout(false);
      setLoadingStartTime(null);
      setSelectedDeviceIds(new Set());
      isInitializedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  }, [isOpen]);

  // Initialize selected devices when showing confirmation (only once when opening)
  useEffect(() => {
    if (showConfirmation && selectedAction && !isInitializedRef.current) {
      const eligibleDevices =
        selectedAction === "LOCK_DOWN"
          ? eligibleDevicesForLockdown
          : eligibleDevicesForRelease;

      // Log what we're working with
      logger.log("[InstallationActionsModal] 🔍 Modal opened with:");
      logger.log("  selectedAction:", selectedAction);
      logger.log("  eligibleForLockdown (count):", eligibleForLockdown);
      logger.log("  eligibleForRelease (count):", eligibleForRelease);
      logger.log(
        "  eligibleDevicesForLockdown length:",
        eligibleDevicesForLockdown.length
      );
      logger.log(
        "  eligibleDevicesForRelease length:",
        eligibleDevicesForRelease.length
      );
      logger.log(
        "  eligibleDevices (selected array) length:",
        eligibleDevices.length
      );
      logger.log(
        "  eligibleDevices detail:",
        eligibleDevices.map((d) => ({
          deviceId: d.deviceId,
          displayName: d.displayName,
          lockdownState: d.lockdownState,
        }))
      );

      // Check all devices by default, excluding models that don't support lockdown
      const allDeviceIds = new Set(
        eligibleDevices
          .filter(
            (device) =>
              selectedAction !== "LOCK_DOWN" || supportsLockdown(device.model)
          )
          .map((device) => device.deviceId)
      );
      logger.log(
        `[InstallationActionsModal] 🔄 Initializing selection with ${allDeviceIds.size} devices`
      );
      setSelectedDeviceIds(allDeviceIds);
      isInitializedRef.current = true;
    }
  }, [
    showConfirmation,
    selectedAction,
    eligibleDevicesForLockdown,
    eligibleDevicesForRelease,
    eligibleForLockdown,
    eligibleForRelease,
  ]);

  const handleRetry = () => {
    setSyncTimeout(false);
    setLoadingStartTime(Date.now());
    if (onRetrySync) {
      onRetrySync();
    }
  };

  if (!isOpen) {
    return null;
  }

  const handleActionClick = (actionType) => {
    // Check if there are eligible devices for this action
    const eligibleCount =
      actionType === "LOCK_DOWN" ? eligibleForLockdown : eligibleForRelease;

    if (eligibleCount === 0) {
      // Show informational message instead of confirmation
      setSelectedAction(actionType);
      setShowConfirmation(true);
      return;
    }

    setSelectedAction(actionType);
    setShowConfirmation(true);
  };

  const handleConfirm = async () => {
    const selectedIds = Array.from(selectedDeviceIds);
    logger.log(
      `[InstallationActionsModal] 🎯 User confirmed action with ${selectedIds.length} device(s) selected`
    );
    logger.log(
      `[InstallationActionsModal] 📋 Selected device IDs:`,
      selectedIds
    );

    setIsExecuting(true);
    try {
      await onExecuteAction(selectedAction, selectedIds);
    } finally {
      setIsExecuting(false);
      setShowConfirmation(false);
      setSelectedAction(null);
      setSelectedDeviceIds(new Set());
      onClose();
    }
  };

  const handleToggleDevice = (deviceId) => {
    setSelectedDeviceIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(deviceId)) {
        newSet.delete(deviceId);
        logger.log(
          `[InstallationActionsModal] ☐ Unchecked device: ${deviceId}`
        );
      } else {
        newSet.add(deviceId);
        logger.log(`[InstallationActionsModal] ☑ Checked device: ${deviceId}`);
      }
      logger.log(
        `[InstallationActionsModal] 📊 Total selected: ${newSet.size}`
      );
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const eligibleDevices =
      selectedAction === "LOCK_DOWN"
        ? eligibleDevicesForLockdown
        : eligibleDevicesForRelease;
    const allDeviceIds = new Set(
      eligibleDevices
        .filter(
          (device) =>
            selectedAction !== "LOCK_DOWN" || supportsLockdown(device.model)
        )
        .map((device) => device.deviceId)
    );
    setSelectedDeviceIds(allDeviceIds);
  };

  const handleDeselectAll = () => {
    setSelectedDeviceIds(new Set());
  };

  const handleCancel = () => {
    setShowConfirmation(false);
    setSelectedAction(null);
  };

  const getActionLabel = (actionType) => {
    return actionType === "LOCK_DOWN" ? "Lockdown" : "Release Lockdown";
  };

  return (
    <div className="installation-actions-overlay" onClick={onClose}>
      <div
        className="installation-actions-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="installation-actions-header">
          <h2>Site-Wide Actions</h2>
          <button className="close-button" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="installation-actions-content">
          {showComingSoon ? (
            <div className="confirmation-dialog">
              <div className="confirmation-icon">📦</div>
              <h3>Coming Soon</h3>
              <p className="confirmation-message">
                To be implemented in a future release.
              </p>
              <div className="confirmation-buttons">
                <button
                  className="confirm-button execute"
                  onClick={() => setShowComingSoon(false)}
                >
                  Close
                </button>
              </div>
            </div>
          ) : !allDeviceStatesLoaded ? (
            // FIX #2 & OPTION D: Show loading state or timeout error
            <div className="confirmation-dialog">
              {syncTimeout ? (
                // Timeout error state
                <>
                  <div
                    className="confirmation-icon"
                    style={{ fontSize: "48px" }}
                  >
                    ⚠️
                  </div>
                  <h3>Sync Timeout</h3>
                  <p className="confirmation-message">
                    Device state sync is taking longer than expected for{" "}
                    <strong>{installationName}</strong>.
                  </p>
                  <p className="confirmation-note">
                    This may be due to network issues or a temporary server
                    delay. You can retry the sync or close this modal and try
                    again later.
                  </p>
                  <div className="confirmation-buttons">
                    <button className="confirm-button cancel" onClick={onClose}>
                      Close
                    </button>
                    <button
                      className="confirm-button execute"
                      onClick={handleRetry}
                    >
                      Retry Sync
                    </button>
                  </div>
                </>
              ) : (
                // Loading state with spinner
                <>
                  <div
                    className="confirmation-icon"
                    style={{ fontSize: "144px" }}
                  >
                    <div className="hourglass-flip">⏳</div>
                  </div>
                  <h3>Loading Device States...</h3>
                  <p className="confirmation-message">
                    Syncing device states for all devices in{" "}
                    <strong>{installationName}</strong>.
                  </p>
                  <p className="confirmation-note">
                    Please wait while we load the latest device information.
                  </p>
                </>
              )}
            </div>
          ) : !showConfirmation ? (
            <>
              <div className="installation-actions-info">
                <p className="installation-name">
                  <strong>{installationName}</strong>
                </p>
                <p className="device-count-info">
                  {deviceCount} {deviceCount === 1 ? "device" : "devices"} in
                  this site
                </p>
              </div>

              <p className="lockdown-disclaimer">
                Manual and Interconnect Deadbolt locks do not support lockdown
                as a feature and cannot be selected for lockdown.
              </p>

              <div className="installation-actions-buttons">
                <button
                  className="lockdown-action-button lockdown"
                  onClick={() => handleActionClick("LOCK_DOWN")}
                  disabled={deviceCount === 0}
                >
                  <span className="action-icon">🚨</span>
                  <span className="action-text">
                    Lockdown the Current Site
                  </span>
                </button>

                <button
                  className="lockdown-action-button release"
                  onClick={() => handleActionClick("FREEZE")}
                  disabled={deviceCount === 0}
                >
                  <span className="action-icon">🔓</span>
                  <span className="action-text">Release Lockdown</span>
                </button>

                <button
                  className="lockdown-action-button firmware"
                  onClick={() => setShowComingSoon(true)}
                  disabled={deviceCount === 0}
                >
                  <span className="action-icon">📦</span>
                  <span className="action-text">
                    Fleetwide Update to Latest Firmware
                  </span>
                </button>
              </div>

              {deviceCount === 0 && (
                <p className="no-devices-warning">
                  No devices available at this site
                </p>
              )}
            </>
          ) : (
            <div className="confirmation-dialog">
              <div className="confirmation-icon">
                {selectedAction === "LOCK_DOWN" ? "🚨" : "🔓"}
              </div>
              {(() => {
                const eligibleCount =
                  selectedAction === "LOCK_DOWN"
                    ? eligibleForLockdown
                    : eligibleForRelease;

                if (eligibleCount === 0) {
                  return (
                    <>
                      <h3>No Eligible Devices</h3>
                      <p className="confirmation-message">
                        There are currently{" "}
                        <strong style={{ color: "#ff8800" }}>
                          0 devices eligible
                        </strong>{" "}
                        for {getActionLabel(selectedAction).toLowerCase()} at{" "}
                        <strong>{installationName}</strong>.
                      </p>
                      <p className="confirmation-note">
                        {selectedAction === "LOCK_DOWN"
                          ? "All devices may already be in lockdown or unavailable."
                          : "No devices are currently in lockdown state."}
                      </p>
                      <div className="confirmation-buttons">
                        <button
                          className="confirm-button execute"
                          onClick={handleCancel}
                        >
                          Close
                        </button>
                      </div>
                    </>
                  );
                }

                const eligibleDevices =
                  selectedAction === "LOCK_DOWN"
                    ? eligibleDevicesForLockdown
                    : eligibleDevicesForRelease;
                const selectedCount = selectedDeviceIds.size;

                return (
                  <>
                    <h3>Confirm {getActionLabel(selectedAction)}</h3>
                    <p className="confirmation-message">
                      Select devices to{" "}
                      {selectedAction === "LOCK_DOWN"
                        ? "lock down"
                        : "release lockdown for"}{" "}
                      at <strong>{installationName}</strong>:
                    </p>

                    <div className="device-selection-container">
                      <div className="device-selection-header">
                        <div className="device-selection-controls">
                          <button
                            className="device-select-button"
                            onClick={handleSelectAll}
                            disabled={isExecuting}
                            type="button"
                          >
                            Select All
                          </button>
                          <button
                            className="device-select-button"
                            onClick={handleDeselectAll}
                            disabled={isExecuting}
                            type="button"
                          >
                            Deselect All
                          </button>
                        </div>
                        <div className="device-selection-count">
                          <strong>{selectedCount}</strong> of{" "}
                          <strong>{eligibleCount}</strong>{" "}
                          {eligibleCount === 1 ? "device" : "devices"} selected
                        </div>
                      </div>

                      <div className="device-list-scroll">
                        {eligibleDevices.map((device) => {
                          const lockdownUnsupported =
                            selectedAction === "LOCK_DOWN" &&
                            !supportsLockdown(device.model);
                          return (
                            <div
                              key={device.deviceId}
                              className={`device-item${lockdownUnsupported ? " device-item--unsupported" : ""}`}
                            >
                              <label className="device-item-label">
                                <input
                                  type="checkbox"
                                  className="device-item-checkbox"
                                  checked={selectedDeviceIds.has(device.deviceId)}
                                  onChange={() =>
                                    handleToggleDevice(device.deviceId)
                                  }
                                  disabled={isExecuting || lockdownUnsupported}
                                />
                                <div className="device-item-info">
                                  <div className="device-name">
                                    {device.displayName || "Unnamed Device"}
                                  </div>
                                  <div className="device-id">
                                    {device.deviceId}
                                  </div>
                                  {lockdownUnsupported && (
                                    <div className="device-unsupported-note">
                                      Lockdown not supported
                                    </div>
                                  )}
                                </div>
                              </label>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <p className="confirmation-note">
                      {selectedAction === "LOCK_DOWN"
                        ? "This will prevent all credential access until the lockdown is released."
                        : "This will restore normal credential access to all devices."}
                    </p>
                    <div className="confirmation-buttons">
                      <button
                        className="confirm-button cancel"
                        onClick={handleCancel}
                        disabled={isExecuting}
                      >
                        Cancel
                      </button>
                      <button
                        className={`confirm-button execute ${
                          selectedAction === "LOCK_DOWN"
                            ? "lockdown"
                            : "release"
                        }`}
                        onClick={handleConfirm}
                        disabled={isExecuting || selectedCount === 0}
                      >
                        {isExecuting
                          ? "Initiating..."
                          : `Yes, ${getActionLabel(
                              selectedAction
                            )} ${selectedCount} Device${
                              selectedCount !== 1 ? "s" : ""
                            }`}
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InstallationActionsModal;
